import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronBackOutline } from "react-icons/io5";
import { TfiSave } from "react-icons/tfi";
import { MdErrorOutline } from "react-icons/md";
import { PacmanLoader } from "react-spinners/";
import { toast } from "react-toastify";
import { supabase } from "../ReusableComponents/supabaseClient";
import StatusBadge from "../ReusableComponents/StatusBadge";
import moment from "moment-timezone";
import { 
  getScoreClass, 
  isPlayerSelected, 
  validateTeamSelection, 
  validateTieBreaker, 
  getChangedLineItems,
  updateTieBreakerScore,
  updateLineItem,
  // fetchUpdatedTeam
} from "./TeamViewFormHelpers";
import { logError } from '../Utilities/errorLogger';

const TeamViewFormComponent = ({ 
  records = [], 
  isEditable: initialEditable = false, 
  poolData, 
  setSpinner, 
  setFormError, 
  setRefetchTrigger, 
  playerTournaments = [] 
}) => {
  const navigate = useNavigate();
  
  // State hooks
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [tieBreakerScore, setTieBreakerScore] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [playerFixtures, setPlayerFixtures] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [canShowEditButton, setCanShowEditButton] = useState(false);
  const [lineItems, setLineItems] = useState([]);

  // Memoized values
  const validRecords = useMemo(() => {
    const sortedRecords = Array.isArray(records) ? 
      [...records].sort((a, b) => (a.display_order || 0) - (b.display_order || 0)) : 
      [];
    return sortedRecords;
  }, [records]);
  
  const teamId = useMemo(() => validRecords[0]?.team?.id, [validRecords]);
  // const showTieBreaker = useMemo(() => poolData?.tournament_status === "In Progress", [poolData]);

  // Initialize selectedPlayers and tieBreakerScore when records change
  useEffect(() => {
    if (!isInitialized && poolData) {
      const maxPlayers = poolData.max_players || 0;
      
      // Create an array of empty players with the correct length
      const players = new Array(maxPlayers).fill(null).map((_, index) => {
        const record = validRecords[index];
        if (record?.player) {
          return {
            id: record.player.id,
            name: record.player.player_name,
            worldRanking: record.player.world_ranking_position,
            lineItemId: record.id,
            displayOrder: record.display_order || index + 1
          };
        }
        return {
          id: "",
          name: "",
          worldRanking: 0,
          lineItemId: null,
          displayOrder: index + 1
        };
      });

      setSelectedPlayers(players);
      
      // Initialize tie breaker score from team data
      if (validRecords[0]?.team?.tie_breaker_score !== undefined) {
        setTieBreakerScore(validRecords[0].team.tie_breaker_score.toString());
      }
      
      setIsInitialized(true);
    }
  }, [poolData, validRecords, isInitialized]);

  // Update state when records change (after save)
  useEffect(() => {
    if (validRecords.length > 0 && isInitialized) {
      const maxPlayers = poolData.max_players || 0;
      
      // Use the already sorted validRecords
      const players = new Array(maxPlayers).fill(null).map((_, index) => {
        const record = validRecords[index];
        if (record?.player) {
          return {
            id: record.player.id,
            name: record.player.player_name,
            worldRanking: record.player.world_ranking_position,
            lineItemId: record.id,
            displayOrder: record.display_order
          };
        }
        return {
          id: "",
          name: "",
          worldRanking: 0,
          lineItemId: null,
          displayOrder: index + 1
        };
      });
      setSelectedPlayers(players);
      
      // Update tie breaker score if it changed
      if (validRecords[0]?.team?.tie_breaker_score !== undefined) {
        setTieBreakerScore(validRecords[0].team.tie_breaker_score.toString());
      }
    }
  }, [validRecords, poolData, isInitialized]);

  const handleSave = useCallback(async (e) => {
    e.preventDefault();

    // Validate team selection
    if (!validateTeamSelection(selectedPlayers)) {
      return;
    }

    const showTieBreakerRow = poolData.tie_breaker === "Winning Score";

    // Validate tie breaker if required
    if (!validateTieBreaker(showTieBreakerRow, tieBreakerScore)) {
      return;
    }

    setSpinner(true);
    setFormError("");

    try {
      // Get the changes first
      const changes = getChangedLineItems(selectedPlayers, validRecords);
      
      // Check if we have any changes to save
      const hasTieBreakerChange = showTieBreakerRow && tieBreakerScore !== validRecords[0]?.team?.tie_breaker_score?.toString();
      const hasPlayerChanges = changes.length > 0;

      if (!hasPlayerChanges && !hasTieBreakerChange) {
        toast.info("No changes to save", { theme: "dark" });
        setIsEditing(false);
        setSpinner(false);
        return;
      }

      const userId = localStorage.getItem("userId");
      if (!userId) {
        toast.error("User not logged in", { theme: "dark" });
        setSpinner(false);
        return;
      }

      // Update tie breaker if needed
      if (hasTieBreakerChange) {
        await updateTieBreakerScore(teamId, tieBreakerScore);
      }

      // Save player changes sequentially to prevent duplicate line items
      if (hasPlayerChanges) {
        for (const change of changes) {
          await updateLineItem(teamId, poolData.id, userId, change);
        }
      }

      // Only update UI after all changes are complete
      setRefetchTrigger(prev => prev + 1);
      setIsEditing(false);
      toast.success("Team updated successfully!", { theme: "dark" });
    } catch (error) {
      console.error("Error saving team:", error);
      await logError({
        filePath: 'Team/TeamViewForm.js',
        functionName: 'handleSave',
        errorType: 'Team Update Error',
        errorMessage: error.message,
        stackTrace: error.stack,
        requestData: { 
          teamId,
          selectedPlayers,
          tieBreakerScore,
          poolId: poolData.id 
        }
      });
      toast.error(error.message || "Error saving team. Please try again.", { theme: "dark" });
      setFormError(error.message || "Error saving team. Please try again.");
    } finally {
      setSpinner(false);
    }
  }, [
    selectedPlayers,
    tieBreakerScore,
    validRecords,
    teamId,
    poolData,
    setSpinner,
    setFormError,
    setRefetchTrigger
  ]);

  useEffect(() => {
    if (poolData && isInitialized) {
      setIsLoading(false);
    }
  }, [poolData, isInitialized]);

  useEffect(() => {
    if (!poolData) {
      setCanShowEditButton(false);
      return;
    }

    // Use the initialEditable prop from parent (Team.js already checks ownership + tournament timing)
    if (initialEditable) {
      // Double-check with time
      const timezone = poolData.time_zone || poolData.tournament_fixture?.time_zone || "America/New_York";
      const now = moment().tz(timezone);
      let isBeforeStart = true;

      if (poolData.first_tee_time) {
        isBeforeStart = now.isBefore(moment.tz(poolData.first_tee_time, timezone));
      } else if (poolData.tournament_fixture?.start_date) {
        isBeforeStart = now.isBefore(moment.tz(poolData.tournament_fixture.start_date, timezone).startOf("day").add(6, "hours"));
      }

      setCanShowEditButton(isBeforeStart);

      if (!isBeforeStart && isEditing) {
        setIsEditing(false);
        toast.info("Tournament has started - editing is no longer available", { theme: "dark" });
      }
    } else {
      setCanShowEditButton(false);
    }

    const interval = setInterval(() => {
      if (!poolData.first_tee_time) return;
      const timezone = poolData.time_zone || poolData.tournament_fixture?.time_zone || "America/New_York";
      const now = moment().tz(timezone);
      const teeTime = moment.tz(poolData.first_tee_time, timezone);
      if (now.isAfter(teeTime)) {
        setCanShowEditButton(false);
        if (isEditing) setIsEditing(false);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [poolData, initialEditable, isEditing]);

  const ownerName = useMemo(() => validRecords[0]?.team?.owner?.full_name || "Unknown", [validRecords]);

  useEffect(() => {
    if (playerTournaments.length > 0) {
      const fixtures = playerTournaments.map((item) => ({
        key: item.id,
        value: `${item.player.player_name} (${item.player.world_ranking_position})`,
        playerId: item.player.id,
        playerName: item.player.player_name,
        worldRanking: item.player.world_ranking_position,
      })).sort((a, b) => a.playerName.localeCompare(b.playerName));

      setPlayerFixtures(fixtures);
    }
  }, [playerTournaments]);

  const handlePlayerSelect = (index, event) => {
    const option = event.target.options[event.target.selectedIndex];
    if (!option) return;

    const playerId = Number(option.getAttribute("data-player-id"));
    const playerName = option.getAttribute("data-player-name");
    const worldRanking = Number(option.getAttribute("data-world-ranking"));

    if (playerId && playerName && !isNaN(worldRanking)) {
      // Get the current line item ID for this position
      const currentLineItemId = validRecords[index]?.id;
      const currentDisplayOrder = validRecords[index]?.display_order || index + 1;

      const newPlayer = {
        id: playerId,
        name: playerName,
        worldRanking: worldRanking,
        lineItemId: currentLineItemId,
        displayOrder: currentDisplayOrder
      };

      const newPlayers = [...selectedPlayers];
      newPlayers[index] = newPlayer;

      setSelectedPlayers(newPlayers);
    }
  };

  // Fetch initial line items data
  useEffect(() => {
    const fetchLineItems = async () => {
      if (!teamId) {
        // If no teamId, create empty line items based on max_players
        if (poolData?.max_players) {
          const emptyLineItems = new Array(poolData.max_players).fill(null).map((_, index) => ({
            id: null,
            player: null,
            tournament_position: "--",
            total_score_to_par: "--",
            players_tournament_status: null,
            display_order: index + 1
          }));
          setLineItems(emptyLineItems);
        }
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('teamLineItem')
          .select(`
            id,
            player:playersFixture!teamLineItem_player_fkey (
              player_name,
              world_ranking_position
            ),
            tournament_position,
            total_score_to_par,
            players_tournament_status,
            display_order
          `)
          .eq('team', teamId)
          .order('display_order', { ascending: true });

        if (error) throw error;

        if (data && data.length > 0) {
          setLineItems(data);
        } else if (poolData?.max_players) {
          // Create empty line items based on max_players
          const emptyLineItems = new Array(poolData.max_players).fill(null).map((_, index) => ({
            id: null,
            player: null,
            tournament_position: "--",
            total_score_to_par: "--",
            players_tournament_status: null,
            display_order: index + 1
          }));
          setLineItems(emptyLineItems);
        }
      } catch (error) {
        await logError({
          filePath: "Team/TeamViewForm.js",
          functionName: "fetchLineItems",
          errorType: "Error",
          errorMessage: error.message,
          stackTrace: error.stack,
        });
        console.error("Error fetching line items:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (poolData) {
      fetchLineItems();
    }
  }, [teamId, poolData]);

  // Handle line items updates
  const handleLineItemsUpdate = (updatedData) => {
    setLineItems(updatedData);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-14 bg-gray-200 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  const showTieBreakerRow = poolData.tie_breaker === "Winning Score";
  const poolName = poolData.name || "Pool";

  const resetToOriginalData = () => {
    // Map only the existing records from validRecords
    const players = validRecords.map(record => {
      if (record?.player) {
        return {
          id: record.player.id,
          name: record.player.player_name,
          worldRanking: record.player.world_ranking_position,
          lineItemId: record.id,
        };
      }
      return null;
    });

    // Fill remaining slots if needed
    while (players.length < poolData.max_players) {
      players.push({
        id: "",
        name: "",
        worldRanking: 0,
        lineItemId: null,
      });
    }

    setSelectedPlayers(players);
    
    // Reset tie breaker score
    setTieBreakerScore(validRecords[0]?.team?.tie_breaker_score || "");
  };

  const handleCancel = () => {
    resetToOriginalData();
    setIsEditing(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20 mb-24 md:mb-6">
      {/* Green Header */}
      <div className="bg-gradient-to-r from-primary to-primary-light rounded-2xl p-6 mb-6 shadow-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-green-200 hover:text-white transition-colors">
              <IoChevronBackOutline className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">{poolName}</h1>
              <p className="text-green-200 text-sm mt-0.5">Team: {ownerName}</p>
            </div>
          </div>
          {canShowEditButton && (
            <button
              onClick={() => isEditing ? handleCancel() : setIsEditing(true)}
              className={`py-2 px-4 rounded-xl font-semibold text-sm transition-all ${
                isEditing
                  ? "bg-white/20 text-white hover:bg-white/30"
                  : "bg-white text-primary hover:bg-green-50 shadow-sm"
              }`}
            >
              {isEditing ? "Cancel" : "Edit Team"}
            </button>
          )}
        </div>

        {/* Stats inside header */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
          {[
            { label: "Tournament", value: <StatusBadge status={poolData.tournament_status || 'Not Started'} /> },
            { label: "Round", value: <StatusBadge status={poolData.round_status || 'Not Started'} /> },
            { label: "Current Round", value: poolData.current_round || '--' },
            { label: "Cut Line", value: poolData.cut_value || '--' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white/15 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-[10px] font-semibold text-green-200 uppercase tracking-wider">{stat.label}</p>
              <div className="mt-1 flex justify-center">
                {typeof stat.value === "string" || typeof stat.value === "number"
                  ? <p className="text-sm font-bold text-white">{stat.value}</p>
                  : stat.value
                }
              </div>
            </div>
          ))}
        </div>
      </div>

      {showTieBreakerRow && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mt-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">Tie Breaker Score</span>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={tieBreakerScore}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || parseInt(value) >= 0) setTieBreakerScore(value);
                  }}
                  placeholder="e.g. 276"
                  className="w-28 px-3 py-2 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm text-center"
                />
                <span className="text-xs text-gray-400">Total strokes</span>
              </div>
            ) : (
              <span className="text-lg font-bold text-gray-900">{tieBreakerScore || "--"}</span>
            )}
          </div>
        </div>
      )}

      {/* Player Cards (visible on all screens) */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-100">
          {selectedPlayers.map((player, index) => {
            const currentPlayerId = player?.id?.toString() || "";
            const isPlayerEmpty = !player?.name;
            return (
              <div key={index} className="px-5 py-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Player {index + 1}</span>
                  {!isEditing && player?.worldRanking > 0 && (
                    <span className="text-xs text-gray-500">WR #{player.worldRanking}</span>
                  )}
                </div>

                {isEditing ? (
                  <select
                    className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-900 bg-white text-sm"
                    onChange={(e) => handlePlayerSelect(index, e)}
                    value={currentPlayerId}
                  >
                    <option value="">Select a Player</option>
                    {playerFixtures.map((fixture) => {
                      const isSelected = currentPlayerId === fixture.playerId.toString();
                      return (
                        <option
                          key={fixture.key}
                          value={fixture.playerId}
                          data-player-id={fixture.playerId}
                          data-player-name={fixture.playerName}
                          data-world-ranking={fixture.worldRanking}
                          disabled={!isSelected && isPlayerSelected(fixture.playerId, selectedPlayers, index)}
                        >
                          {fixture.value}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <p className={`text-base font-semibold ${isPlayerEmpty ? "text-red-500" : "text-gray-900"}`}>
                    {isPlayerEmpty ? (
                      <span className="flex items-center gap-1">
                        <MdErrorOutline className="w-4 h-4" /> Not Selected
                      </span>
                    ) : player.name}
                  </p>
                )}

                {!isEditing && !isPlayerEmpty && (
                  <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500">
                    {validRecords[index]?.tournament_position && (
                      <span>Pos: <span className="font-medium text-gray-900">{validRecords[index].tournament_position}</span></span>
                    )}
                    {validRecords[index]?.total_score_to_par && validRecords[index].total_score_to_par !== "--" && (
                      <span>Score: <span className={`font-medium ${
                        parseInt(validRecords[index].total_score_to_par) < 0 ? "text-red-600" :
                        parseInt(validRecords[index].total_score_to_par) > 0 ? "text-blue-600" : "text-gray-900"
                      }`}>{validRecords[index].total_score_to_par}</span></span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {isEditing && (
        <div className="mt-6">
          <button
            onClick={handleSave}
            className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-bold py-3.5 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            <TfiSave className="w-4 h-4" /> Save Team
          </button>
        </div>
      )}
    </div>
  );
};

const TeamViewForm = React.memo(TeamViewFormComponent);

export default TeamViewForm;
